import { FeedbackAlert as Alert } from "src/shared/ui/src/feedback/FeedbackAlert";
import { Typography } from "@mui/material";
import dico from "../../shared/util/src/language/dico";
import {
  entryDisplayValue,
  type EntryCell,
  type EntryReceipt,
} from "../../shared/util/src/athlete-entry";
import TicketDecision from "./TicketDecision";

export default function TicketActivity({
  command: c,
  cells,
  onClose,
}: {
  command: { id: string; status: string; receipt?: EntryReceipt };
  cells: EntryCell[];
  onClose?: () => void;
}) {
  const t = dico().athleteEntry;
  const duration = (ms: number) => {
    const seconds = Math.max(0, Math.ceil(ms / 1000));
    return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
  };
  const message = (error: string) =>
    t.errors[error as keyof typeof t.errors] || t.error;
  return (
    <Alert
      key={c.id}
      onClose={onClose}
      closeText={t.close}
      variant="outlined"
      sx={{
        borderRadius: 3,
        bgcolor: "background.paper",
        ".MuiAlert-message": { width: "100%" },
      }}
      severity={
        c.receipt?.decision === "accepted"
          ? "success"
          : c.receipt?.decision === "refused"
            ? "error"
            : c.receipt?.status === "applied"
              ? ["late", "unknown"].includes(c.receipt.timing) ||
                (c.receipt.reviewRequired && !c.receipt.decision)
                ? "warning"
                : "success"
              : "info"
      }
    >
      {c.receipt?.signature && (
        <Typography variant="caption" component="div">
          {t.ticketSigned} · {t.signatureConfirmedAt} :{" "}
          {new Date(c.receipt.signature.confirmedAt).toLocaleString()}
        </Typography>
      )}
      {c.receipt?.decision ? (
        <TicketDecision
          receipt={c.receipt}
          cell={cells.find((cell) => cell.field === c.receipt?.field)}
        />
      ) : c.receipt ? (
        <>
          {c.receipt.updates?.length ? (
            <>
              <Typography variant="body2" fontWeight={600}>
                {t.equipmentSettings}
              </Typography>
              <Typography variant="body2">
                {c.receipt.updates
                  .map(
                    (update) =>
                      `${update.label || (t[update.field as keyof typeof t] as string) || update.field} : ${entryDisplayValue(update.field, update.value, update.valueLabel)}`,
                  )
                  .join(" · ")}
              </Typography>
            </>
          ) : (
            <>
              {c.receipt.label ||
                (t[c.receipt.field as keyof typeof t] as string)}{" "}
              :{" "}
              {entryDisplayValue(
                c.receipt.field,
                c.receipt.value,
                c.receipt.valueLabel,
              )}
            </>
          )}{" "}
          -{" "}
          {c.receipt.decision === "accepted"
            ? t.acceptedEntry
            : c.receipt.decision === "refused"
              ? c.receipt.restoreApplied === false
                ? t.refusedEntryManual
                : t.refusedEntry
              : c.receipt.status === "applied"
                ? t.applied
                : `${t.rejected} : ${message(c.receipt.reason || "")}`}
          {c.receipt.reviewRequired && !c.receipt.decision && (
            <Typography variant="body2">
              {c.receipt.reviewReason === "custom-attempt-change"
                ? t.customAttemptChange
                : t.thirdAttemptChange}
            </Typography>
          )}
          {c.receipt.timing === "late" && (
            <Typography>
              {t.late} (+{duration(c.receipt.lateByMs || 0)})
              <Typography variant="body2" component="span" display="block">
                {!c.receipt.decision && t.lateReceiptHelp}
              </Typography>
            </Typography>
          )}
          {c.receipt.status === "rejected" &&
            (c.receipt.updates?.length ? c.receipt.updates : [c.receipt]).map(
              (update) => (
                <Typography key={update.field} variant="body2">
                  {update.label ||
                    (t[update.field as keyof typeof t] as string)}{" "}
                  · {t.current}:{" "}
                  {cells.find((cell) => cell.field === update.field)?.value ??
                    "-"}{" "}
                  · {t.proposed}:{" "}
                  {entryDisplayValue(
                    update.field,
                    update.value,
                    update.valueLabel,
                  )}
                </Typography>
              ),
            )}
          {c.receipt.reviewedAt && (
            <Typography variant="body2">
              {t.secretaryFeedback}: {c.receipt.reviewNote || t.reviewed}
            </Typography>
          )}
          {c.receipt.timing === "unknown" && (
            <Typography>{t.unknownTime}</Typography>
          )}
        </>
      ) : c.status === "cancelled" ? (
        t.cancelled
      ) : (
        t.pending
      )}
    </Alert>
  );
}
